// Render quality governor: full quality while frames fit 60 fps; when they persistently don't, a rung down
// the ladder (resolution, then the shadow map) is probed and kept only if it measurably buys frame rate —
// so a 30/50 Hz display, a browser rAF cap or a CPU-bound machine keep full quality, while a GPU-bound one
// descends exactly as far as it helps. Nothing can tell "capped" from "GPU-bound" without trying (browsers
// have no portable GPU timer, and CPU-side frame work looks the same in both cases), so every level change
// is a probe with a verification, and a rung that failed is retried only after a doubling backoff instead
// of being written off: one noisy check (a teamfight) then costs one backoff, not the whole match. The same
// probe runs upward when stuck below 60 with nothing left to try, so a rung kept by a lucky check is given
// back. Pure state machine over frame intervals (no DOM, no renderer) so the hysteresis is unit-tested;
// matchView applies the levels.

export type QualityLevel = { scale: number; shadowLow: boolean };

export const LEVELS: readonly QualityLevel[] = [
  { scale: 1, shadowLow: false },
  { scale: 0.85, shadowLow: false },
  { scale: 0.7, shadowLow: false },
  { scale: 0.55, shadowLow: false },
  { scale: 0.55, shadowLow: true },
];

export const GOVERNOR = {
  windowMs: 1500,        // frames are averaged per window this long, whatever the frame rate
  windows: 2,            // consecutive windows per verdict: to trigger a probe, and to verify it
  downFps: 55,           // a window averaging below this is "bad"
  upFps: 59,             // a window averaging above this is "good"
  settleMs: 3000,        // no verdicts right after start or a level change: loading/reallocation noise
  stallMs: 250,          // an isolated interval longer than this is a hidden tab or a stall, not load
  gainRatio: 0.08,       // a rung down must buy this fraction of fps to stay; a rung up may cost at most this
  failWithinMs: 12000,   // a step down this soon after a step up means the up step failed
  backoffMs: 30000,      // a failed rung is retried after this long, doubling per repeat…
  maxBackoffMs: 600000,  // …up to this
};

export function createGovernor(cfg = GOVERNOR) {
  const top = LEVELS.length - 1;
  let level = 0, forced: number | null = null;
  let acc = { ms: 0, n: 0 }, prevLong = false, hidden = false, resettle = false;
  let bad: number[] = [], good: number[] = [];
  let probe: { from: number; base: number; got: number[]; held: number } | null = null;
  let changedAt = -Infinity, heldSince = -Infinity, startAt: number | null = null, lastUp: { at: number; to: number } | null = null;
  // a blocked rung remembers the fps it failed at: a materially heavier load since is new evidence.
  // earned: the fps the rung above gave when a rung was kept — a rung that stops beating it is given back
  const block = new Map<number, { until: number; fps: number }>(), backoff = new Map<number, number>(), earned = new Map<number, number>();
  const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

  function move(to: number, now: number) {
    level = to; changedAt = heldSince = now; acc = { ms: 0, n: 0 }; bad = []; good = [];
    return level;
  }
  function fail(rung: number, now: number, fps: number) {
    const b = backoff.get(rung) ?? cfg.backoffMs;
    block.set(rung, { until: now + b, fps });
    backoff.set(rung, Math.min(cfg.maxBackoffMs, b * 2));
  }
  function open(rung: number, now: number, fps: number) {
    const b = block.get(rung);
    return !b || now >= b.until || (rung > level && fps < b.fps * (1 - cfg.gainRatio));
  }
  function start(to: number, now: number, base: number) {
    probe = { from: level, base, got: [], held: heldSince };
    if (to < level) { lastUp = { at: now, to }; earned.delete(to); }
    else if (lastUp?.to === level && now - lastUp.at < cfg.failWithinMs) { fail(level, now, base); lastUp = null; }
    return move(to, now);
  }

  return {
    get level() { return forced ?? level; },
    /** Pin a level (debug / ?quality=N), or null (or anything not a number) to hand control back. */
    force(l: number | null) {
      forced = l === null || !Number.isFinite(l) ? null : Math.max(0, Math.min(top, Math.round(l)));
      if (probe) level = probe.from;  // an unverified rung is not kept
      probe = null; acc = { ms: 0, n: 0 }; bad = []; good = []; resettle = true;
    },
    /** One frame interval in ms. Returns the new level when it changed this frame, else null. */
    sample(frameMs: number, now: number, visible = true): number | null {
      startAt ??= now;
      if (forced !== null || !visible) { acc = { ms: 0, n: 0 }; hidden = !visible; return null; }
      if (resettle) { resettle = false; changedAt = now; }
      // rAF does not fire in a hidden tab: the first frame back spans the whole gap. A run of long
      // frames, though, is a machine that slow.
      const long = frameMs > cfg.stallMs, skip = hidden || (long && !prevLong);
      hidden = false; prevLong = long;
      if (skip) return null;
      acc.ms += frameMs; acc.n++;
      if (acc.ms < cfg.windowMs) return null;
      const fps = acc.n * 1000 / acc.ms;
      acc = { ms: 0, n: 0 };
      if (now - startAt < cfg.settleMs || now - changedAt < cfg.settleMs) return null;
      if (probe) {
        probe.got.push(fps);
        if (probe.got.length < cfg.windows) return null;
        const { from, base, got, held } = probe, g = mean(got);
        probe = null;
        const ok = level > from ? g >= base * (1 + cfg.gainRatio) : g >= cfg.downFps || g >= base * (1 - cfg.gainRatio);
        // "didn't help" evidence accumulates over a match: a rung down keeps its backoff even when it passes,
        // and a rung left upward for free was not earning its keep either
        if (ok) { if (level > from) earned.set(level, base); else { backoff.delete(level); fail(from, now, base); } return null; }
        fail(level, now, base);
        lastUp = null;
        move(from, now);
        heldSince = held;  // a probe and its revert do not restart the wait at the level it left
        return level;
      }
      if (fps < cfg.downFps) { bad.push(fps); good = []; } else if (fps > cfg.upFps) { good.push(fps); bad = []; } else { bad = []; good = []; }
      if (bad.length > cfg.windows) bad.shift();
      if (good.length > cfg.windows) good.shift();
      if (bad.length >= cfg.windows) {
        const base = mean(bad);
        if (level < top && open(level + 1, now, base)) return start(level + 1, now, base);
        // give a rung back when it no longer earns its keep, or when stuck here a while with nothing left
        // to try: either way the rung above may be free after all
        const e = earned.get(level), idle = now - heldSince >= cfg.backoffMs;
        if (level > 0 && (idle || (e !== undefined && base < e * (1 + cfg.gainRatio))) && open(level - 1, now, base)) return start(level - 1, now, base);
        return null;
      }
      if (good.length >= cfg.windows && level > 0 && open(level - 1, now, 0)) return start(level - 1, now, mean(good));
      return null;
    },
  };
}
